import React, { createContext, useContext, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useNormalizedSearchParams } from "./components/useNormalizedSearchParams";
import FreeCreditsDialog from "./components/FreeCreditsDialog";

// Generic
import Home from './pages/Home';
import SignUp from "./pages/SignUp";
import Verification from "./pages/Verification";
import ForgetPassword from "./pages/ForgetPassword";
import Dashboard from './pages/Dashboard';
import ProtectedRoute from './routing/ProtectedRoute';
import RouteAccessGuard from './routing/RouteAccessGuard';
import PageNotFound from './components/PageNotFound';

// Projects

// Keywords Group

// Keywords

// Domains

// Templates

// AI Writer

// AI Site Builder
import { usePublicConfig } from './services/publicConfig';

// Billing and Plan Purchase

// Domains UpTime
import { CreditsProvider, useCredits } from './contexts/CreditsContext';

// Gates the Customers/Reservations route on the customers_enabled flag (default
// off — mock-data feature). Redirects to the dashboard when disabled, so a
// direct URL can't reach it in production.
const CustomersRouteGuard = ({ children }) => {
  const { customersEnabled, flagsLoaded } = useCredits();
  // customersEnabled starts false and turns true only once Remote Config answers.
  // Deciding before that bounced a direct load of /dashboard/customers to Projects.
  if (!flagsLoaded) return <RouteFallback />;
  return customersEnabled ? children : <Navigate to="/dashboard" replace />;
};
import ErrorBoundary from "./components/ErrorBoundary";
import { Box, Skeleton } from '@mui/material';

/**
 * What fills the screen while a route's chunk arrives.
 *
 * A skeleton, not a spinner — the convention everywhere else in this app — and
 * sized to the page rather than the viewport centre, so the shell does not
 * collapse to nothing and then bounce when the chunk lands. On a warm cache this
 * is never seen; on a cold one it is a page taking shape rather than a blank.
 */
function RouteFallback() {
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Skeleton variant="text" width={220} height={38} sx={{ mb: 1 }} />
      {/* Capped, not fixed: a 340px bar is wider than a phone's content box
          (296px at 360 wide), so the pane scrolled sideways while a chunk loaded. */}
      <Skeleton variant="text" height={22} sx={{ width: '100%', maxWidth: 340, mb: 3 }} />
      <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: 2 }} />
      <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
    </Box>
  );
}


/*
 * ROUTE-LEVEL CODE SPLITTING.
 *
 * Every page was imported eagerly, so opening the login screen downloaded the
 * whole product: 6.6 MB raw, 1.84 MB gzipped, including recharts, xlsx and
 * html2pdf for anyone who never opens a chart or exports anything. One bundle,
 * 108 routes, and a first paint that waited for all of it.
 *
 * The auth screens and the dashboard shell stay eager on purpose — making the
 * login page lazy adds a round trip before the user sees anything, which is the
 * opposite of the point.
 */
const UserProfile = lazy(() => import('./pages/userProfile'));
const GoogleAnalytics = lazy(() => import('./pages/GoogleAnalytics'));
const GoogleAccounts = lazy(() => import('./pages/GoogleAccounts'));
const AnalyticsHome = lazy(() => import('./pages/Analytics/AnalyticsHome'));
const SEOHome = lazy(() => import('./pages/SEO/SEOHome'));
const UptimeHome = lazy(() => import('./pages/Uptime/UptimeHome'));
const DashboardStatistics = lazy(() => import('./pages/DashboardStatistics'));
const ShopifyPrivacyPage = lazy(() => import('./pages/ShopifyPrivacy'));
const Projects = lazy(() => import('./pages/Project/Projects'));
const AddProject = lazy(() => import('./pages/Project/AddProject'));
const EditProject = lazy(() => import('./pages/Project/EditProject'));
const DuplicateProject = lazy(() => import('./pages/Project/DuplicateProject'));
const KeywordsGroup = lazy(() => import('./pages/Keywords Group/KeywordsGroup'));
const AddKeywordsGroup = lazy(() => import('./pages/Keywords Group/AddKeywordsGroup'));
const EditKeywordsGroup = lazy(() => import('./pages/Keywords Group/EditKeywordsGroup'));
const DuplicateKeywordsGroup = lazy(() => import('./pages/Keywords Group/DuplicateKeywordsGroup'));
const ImportKeywordsGroup = lazy(() => import('./pages/Keywords Group/ImportKeywordsGroup'));
const Keywords = lazy(() => import('./pages/Keyword/Keywords'));
const AddKeyword = lazy(() => import('./pages/Keyword/AddKeyword'));
const EditKeyword = lazy(() => import('./pages/Keyword/EditKeyword'));
const DuplicateKeyword = lazy(() => import('./pages/Keyword/DuplicateKeyword'));
const ImportKeyword = lazy(() => import('./pages/Keyword/ImportKeyword'));
const Domains = lazy(() => import('./pages/Domain/Domains'));
const AddDomain = lazy(() => import('./pages/Domain/AddDomain'));
const EditDomain = lazy(() => import('./pages/Domain/EditDomain'));
const DuplicateDomain = lazy(() => import('./pages/Domain/DuplicateDomain'));
const ImportDomain = lazy(() => import('./pages/Domain/ImportDomain'));
const Templates = lazy(() => import('./pages/Template/Templates'));
const AddTemplate = lazy(() => import('./pages/Template/AddTemplate'));
const EditTemplate = lazy(() => import('./pages/Template/EditTemplate'));
const DuplicateTemplate = lazy(() => import('./pages/Template/DuplicateTemplate'));
const AIWriter = lazy(() => import('./pages/AI Content/AIWriter'));
const AIWriterFineTuning = lazy(() => import('./pages/AI Content/AIWriterFineTuning'));
const AIWriterExpress = lazy(() => import('./pages/AI Content/AIWriterExpress'));
const PastArticles = lazy(() => import('./pages/AI Content/PastArticles'));
const EditPastArticle = lazy(() => import('./pages/AI Content/EditPastArticle'));
const PastJobs = lazy(() => import('./pages/AI Content/PastJobs'));
const JobDetailsPage = lazy(() => import('./pages/AI Content/JobDetailsPage'));
const AISiteBuilder = lazy(() => import('./pages/AI Site/AISiteBuilder'));
const AISiteWriter = lazy(() => import('./pages/AI Site/AISiteWriter'));
const AISiteBuilderHTTPS = lazy(() => import('./pages/AI Site/AISiteBuilderHTTPS'));

/**
 * Remount the builder when the SITE in the URL changes.
 *
 * See the note on the route below: XSiteBuilder reads siteId once, at mount, so
 * a same-route param change moves the URL and nothing else. Keying on the param
 * makes the switch a real remount, which is what it is.
 *
 * Only siteId. Other params on that route — step, jobId, previewPath — are
 * handled inside the component and must NOT tear it down, or every step change
 * would throw away the wizard.
 */
function KeyedBySite({ children }) {
  const { search } = useLocation();
  return <React.Fragment key={new URLSearchParams(search).get('siteId') || 'none'}>{children}</React.Fragment>;
}
const XSiteAutopilot = lazy(() => import('./pages/AI Site/XSiteAutopilot'));
/*
 * EDITOR SPIKE — test hosting only. The project check is a build-time constant,
 * so on the production build this is `null` and webpack drops the import: no
 * chunk, no route. (The editor bundle itself is only copied into build-test.)
 */
const EditorSpike = process.env.REACT_APP_PROJECT_ID !== 'seoweb-408ea'
  ? lazy(() => import('./pages/AI Site/editorSpike/EditorSpike'))
  : null;
const PastSites = lazy(() => import('./pages/AI Site/PastSites'));
const Customers = lazy(() => import('./pages/Customers/Customers'));
const PastSiteJobs = lazy(() => import('./pages/AI Site/PastSiteJobs'));
const XSiteJobDetails = lazy(() => import('./pages/AI Site/XSiteJobDetails'));
const PlanPurchase = lazy(() => import('./pages/Billing/PlanPurchase'));
const ShopifyPlanPurchase = lazy(() => import('./pages/Billing/ShopifyPlanPurchase'));
const PlanPurchaseSuccess = lazy(() => import('./pages/Billing/PlanPurchaseSuccess'));
const PlanPurchaseFailure = lazy(() => import('./pages/Billing/PlanPurchaseFailure'));
const DomainOverview = lazy(() => import('./pages/Domain/DomainOverview'));
const ShopifyOAuthRedirect = lazy(() => import('./pages/ShopifyOAuthRedirect'));
const ShopifyInstall = lazy(() => import('./pages/ShopifyInstall'));

// Query Context
const QueryContext = createContext();
export function useQuery() {
  return useContext(QueryContext);
}

// Query Provider (inside Router)
function QueryProvider({ children }) {
  const [query, setQuery] = useNormalizedSearchParams();
  return (
    <QueryContext.Provider value={[query, setQuery]}>
      {children}
    </QueryContext.Provider>
  );
}

// App
/**
 * `/xsite-builder/bulk` existed for one day as its own page before bulk
 * generation moved into Autopilot, where it belonged. Redirects rather than
 * 404s, and carries the query string so a `?bulk=` link resumes its batch.
 *
 * Reads the search from the ROUTER, not window.location: a route element cannot
 * call hooks, which is how the first version ended up reaching around the router
 * — correct under BrowserRouter with no basename, and quietly wrong the moment
 * either of those changes.
 */
function BulkPathRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/dashboard/xsite-builder/autopilot${search}`} replace />;
}

// Autopilot is on wherever backend's xsite_bulk_enabled is (GET /xeo/v1/config) —
// one switch, the same on test and prod; the manual builder otherwise. Nothing
// while the config is still loading, so a deep link isn't bounced.
function AutopilotRoute() {
  const { config, loaded } = usePublicConfig();
  if (!loaded) return null;
  return config.xsite_bulk_enabled ? <XSiteAutopilot /> : <Navigate to="/dashboard/xsite-builder" replace />;
}

function App() {
  return (
    <Router>
      <CreditsProvider>
        <QueryProvider>
          <div className="App">
            <FreeCreditsDialog />
            <ErrorBoundary>
            {/* One fallback for every lazy route. A skeleton rather than a spinner,
                per the repo's own convention — and a full-height one, so the page does
                not collapse to nothing and bounce the layout when the chunk lands. */}
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/verification" element={<Verification />} />
              <Route path="/forget-password" element={<ForgetPassword />} />
              <Route path="/connector/callback" element={<ShopifyOAuthRedirect />} />
              <Route path="/connector" element={<ShopifyInstall />} />
              <Route path="/privacy-policy" element={<ShopifyPrivacyPage />} />
              <Route path="/dashboard/*" element={<ProtectedRoute component={Dashboard} />}>
                {/* DEFAULT ROUTE - Always redirect to projects */}
                <Route path="statistics" element={<DashboardStatistics />} />
                <Route index element={<Navigate to="/dashboard/projects" replace />} />

                {/* Generic Routes - No access control needed */}
                <Route path="profile" element={<UserProfile />} />
                <Route path="google-analytics" element={<GoogleAnalytics />} />
                <Route path="google" element={<GoogleAccounts />} />
                <Route path="analytics" element={<AnalyticsHome />} />
                <Route path="seo" element={<SEOHome />} />
                <Route path="uptime" element={<UptimeHome />} />
                <Route path="plans" element={<PlanPurchase />} />
                {/* Auto top-up is now a dialog on the Plans page; keep the old path working. */}
                <Route path="auto-topup" element={<Navigate to="/dashboard/plans?autotopup=open" replace />} />
                <Route path="connector/plans" element={<ShopifyPlanPurchase />} />
                <Route path="plan-purchase-success" element={<PlanPurchaseSuccess />} />
                <Route path="plan-purchase-failure" element={<PlanPurchaseFailure />} />

                {/* Projects - Protected */}
                <Route path="projects" element={
                  <RouteAccessGuard requiredAccess="projects">
                    <Projects />
                  </RouteAccessGuard>
                } />
                <Route path="projects/add-project" element={
                  <RouteAccessGuard requiredAccess="projects">
                    <AddProject />
                  </RouteAccessGuard>
                } />
                <Route path="projects/edit/:id" element={
                  <RouteAccessGuard requiredAccess="projects">
                    <EditProject />
                  </RouteAccessGuard>
                } />
                <Route path="projects/duplicate/:id" element={
                  <RouteAccessGuard requiredAccess="projects">
                    <DuplicateProject />
                  </RouteAccessGuard>
                } />

                {/* Keywords - Protected */}
                <Route path="keywords" element={
                  <RouteAccessGuard requiredAccess="keywords">
                    <Keywords />
                  </RouteAccessGuard>
                } />
                <Route path="keywords/add-keyword" element={
                  <RouteAccessGuard requiredAccess="keywords">
                    <AddKeyword />
                  </RouteAccessGuard>
                } />
                <Route path="keywords/edit/:id" element={
                  <RouteAccessGuard requiredAccess="keywords">
                    <EditKeyword />
                  </RouteAccessGuard>
                } />
                <Route path="keywords/duplicate/:id" element={
                  <RouteAccessGuard requiredAccess="keywords">
                    <DuplicateKeyword />
                  </RouteAccessGuard>
                } />
                <Route path="keywords/import-keywords" element={
                  <RouteAccessGuard requiredAccess="keywords">
                    <ImportKeyword />
                  </RouteAccessGuard>
                } />

                {/* Keywords Group - Protected */}
                <Route path="keywordsgroup" element={
                  <RouteAccessGuard requiredAccess="keywordsgroup">
                    <KeywordsGroup />
                  </RouteAccessGuard>
                } />
                <Route path="keywordsgroup/add-keywordsgroup" element={
                  <RouteAccessGuard requiredAccess="keywordsgroup">
                    <AddKeywordsGroup />
                  </RouteAccessGuard>
                } />
                <Route path="keywordsgroup/edit/:id" element={
                  <RouteAccessGuard requiredAccess="keywordsgroup">
                    <EditKeywordsGroup />
                  </RouteAccessGuard>
                } />
                <Route path="keywordsgroup/duplicate/:id" element={
                  <RouteAccessGuard requiredAccess="keywordsgroup">
                    <DuplicateKeywordsGroup />
                  </RouteAccessGuard>
                } />
                <Route path="keywordsgroup/import-keywordsgroup" element={
                  <RouteAccessGuard requiredAccess="keywordsgroup">
                    <ImportKeywordsGroup />
                  </RouteAccessGuard>
                } />

                {/* Domains - Protected */}
                <Route path="domains" element={
                  <RouteAccessGuard requiredAccess="domains">
                    <Domains />
                  </RouteAccessGuard>
                } />
                <Route path="domains/add-domain" element={
                  <RouteAccessGuard requiredAccess="domains">
                    <AddDomain />
                  </RouteAccessGuard>
                } />
                <Route path="domains/uptime-domain/:id" element={
                  <RouteAccessGuard requiredAccess="domains">
                    <DomainOverview />
                  </RouteAccessGuard>
                } />
                <Route path="domains/uptime-domain/page-not-found" element={<PageNotFound />} />
                <Route path="domains/edit/:id" element={
                  <RouteAccessGuard requiredAccess="domains">
                    <EditDomain />
                  </RouteAccessGuard>
                } />
                <Route path="domains/duplicate/:id" element={
                  <RouteAccessGuard requiredAccess="domains">
                    <DuplicateDomain />
                  </RouteAccessGuard>
                } />
                <Route path="domains/import-domains" element={
                  <RouteAccessGuard requiredAccess="domains">
                    <ImportDomain />
                  </RouteAccessGuard>
                } />

                {/* Templates - Protected */}
                <Route path="templates" element={
                  <RouteAccessGuard requiredAccess="templates">
                    <Templates />
                  </RouteAccessGuard>
                } />
                <Route path="templates/add-template" element={
                  <RouteAccessGuard requiredAccess="templates">
                    <AddTemplate />
                  </RouteAccessGuard>
                } />
                <Route path="templates/edit/:id" element={
                  <RouteAccessGuard requiredAccess="templates">
                    <EditTemplate />
                  </RouteAccessGuard>
                } />
                <Route path="templates/duplicate/:id" element={
                  <RouteAccessGuard requiredAccess="templates">
                    <DuplicateTemplate />
                  </RouteAccessGuard>
                } />

                {/* AI Writer - Protected */}
                <Route path="article-writer" element={
                  <RouteAccessGuard requiredAccess="article-writer">
                    <AIWriter />
                  </RouteAccessGuard>
                } />
                <Route path="article-writer/manual" element={
                  <RouteAccessGuard requiredAccess="article-writer">
                    <AIWriterFineTuning />
                  </RouteAccessGuard>
                } />
                <Route path="article-writer/autopilot" element={
                  <RouteAccessGuard requiredAccess="article-writer">
                    <AIWriterExpress />
                  </RouteAccessGuard>
                } />
                <Route path="article-writer/autopilot/job-status" element={
                  <RouteAccessGuard requiredAccess="article-writer">
                    <JobDetailsPage />
                  </RouteAccessGuard>
                } />

                <Route path="past-articles" element={
                  <RouteAccessGuard requiredAccess="past-articles">
                    <PastArticles />
                  </RouteAccessGuard>
                } />
                <Route path="past-articles/edit/:id" element={
                  <RouteAccessGuard requiredAccess="past-articles">
                    <EditPastArticle />
                  </RouteAccessGuard>
                } />

                <Route path="past-writing-jobs" element={
                  <RouteAccessGuard requiredAccess="past-writing-jobs">
                    <PastJobs />
                  </RouteAccessGuard>
                } />
                <Route path="past-writing-jobs/detail" element={
                  <RouteAccessGuard requiredAccess="past-writing-jobs">
                    <JobDetailsPage />
                  </RouteAccessGuard>
                } />

                {/* XSite Builder - Protected */}
                <Route path="xsite-builder" element={
                  <RouteAccessGuard requiredAccess="xsite-builder">
                    <AISiteWriter />
                  </RouteAccessGuard>
                } />
                <Route path="xsite-builder/manual/wordpress" element={
                  <RouteAccessGuard requiredAccess="xsite-builder">
                    <AISiteBuilder />
                  </RouteAccessGuard>
                } />
                {/* The one-screen batch test page lived here; Autopilot is the
                    batch flow. Old links go there, ?bulk= and all. */}
                <Route path="xsite-builder/manual/bulk" element={<BulkPathRedirect />} />
                {EditorSpike && (
                  <Route path="xsite-builder/editor-spike" element={
                    <RouteAccessGuard requiredAccess="xsite-builder">
                      <EditorSpike />
                    </RouteAccessGuard>
                  } />
                )}
                {/*
                  * Keyed on the site, so moving between the sites of an Autopilot
                  * batch REMOUNTS the builder.
                  *
                  * Its init() reads siteId from the URL once, at mount — sensible
                  * for a screen that is normally opened at one site and stays
                  * there. The batch switcher changes that param on the same
                  * route, which React Router does not treat as a new element, so
                  * the URL would move and fifteen thousand lines of state would
                  * not. A key is the honest fix: switching sites really is
                  * starting the screen again on a different site.
                  */}
                <Route path="xsite-builder/manual/custom" element={
                  <RouteAccessGuard requiredAccess="xsite-builder">
                    <KeyedBySite><AISiteBuilderHTTPS /></KeyedBySite>
                  </RouteAccessGuard>
                } />
                {/*
                  * THE SAME SCREEN, UNDER AUTOPILOT'S URL.
                  *
                  * A site built by a batch is an ordinary XSite, so it gets the
                  * builder's preview — the AI editor, the live pane, Publish.
                  * But it should not be reached at .../manual/custom: the URL is
                  * the first thing in the address bar and on any link shared, and
                  * telling someone their Autopilot site lives under "manual" is
                  * the same wrong claim the five-step stepper used to make.
                  *
                  * One element, two paths. Nothing is duplicated and the two
                  * cannot drift — ?bulk= is what selects Autopilot's step labels
                  * and the sibling switcher, exactly as it did before.
                  */}
                <Route path="xsite-builder/autopilot/site" element={
                  <RouteAccessGuard requiredAccess="xsite-builder">
                    <KeyedBySite><AISiteBuilderHTTPS /></KeyedBySite>
                  </RouteAccessGuard>
                } />
                {/* Autopilot — one brief, N sites. Same access gate as the manual
                    builder, plus a production gate: the flow is unfinished, so a
                    bookmarked link there redirects instead of opening it. */}
                <Route path="xsite-builder/autopilot" element={
                  <RouteAccessGuard requiredAccess="xsite-builder">
                    <AutopilotRoute />
                  </RouteAccessGuard>
                } />
                {/* Bulk generation lives INSIDE Autopilot — "one brief, N sites"
                    is what Autopilot always was, and its Review & launch step was
                    designed for exactly this runner. This path existed for one day
                    as a separate page; it redirects so any link from then still
                    works, carrying ?bulk= through so a running batch resumes. */}
                <Route path="xsite-builder/bulk" element={<BulkPathRedirect />} />

                <Route path="past-sites" element={
                  <RouteAccessGuard requiredAccess="past-sites">
                    <PastSites />
                  </RouteAccessGuard>
                } />

                <Route path="customers" element={
                  <CustomersRouteGuard>
                    <RouteAccessGuard requiredAccess="customers">
                      <Customers />
                    </RouteAccessGuard>
                  </CustomersRouteGuard>
                } />

                <Route path="past-site-jobs" element={
                  <RouteAccessGuard requiredAccess="past-site-jobs">
                    <PastSiteJobs />
                  </RouteAccessGuard>
                } />
                <Route path="past-site-jobs/:jobId" element={
                  <RouteAccessGuard requiredAccess="past-site-jobs">
                    <XSiteJobDetails />
                  </RouteAccessGuard>
                } />

                {/* Unknown dashboard paths rendered the shell with an empty
                    content area — no message, no way back. */}
                <Route path="*" element={<PageNotFound />} />
              </Route>
              {/* And anything else was a blank white page. */}
              <Route path="*" element={<PageNotFound />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </div>
        </QueryProvider>
      </CreditsProvider>
    </Router>
  );
}

export default App;